import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { database } from '../config/backend';
import { ref, get as rtdbGet } from '../services/phpDataAdapter';
import { markPDFAsViewed } from '../services/pdfSendService';
import { Button } from '../components/ui/Button';
import './Pages.css';

export const ViewPDF: React.FC = () => {
  const { pdfId } = useParams<{ pdfId: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [fileName, setFileName] = useState<string>('');
  const pdfUrlRef = useRef<string | null>(null);

  useEffect(() => {
    async function loadPDF() {
      if (!pdfId) {
        setError('ID do PDF não fornecido');
        setLoading(false);
        return;
      }

      try {
        const pdfRef = ref(database, `pdfDocuments/${pdfId}`);
        const snapshot = await rtdbGet(pdfRef);

        if (!snapshot.exists()) {
          setError('PDF não encontrado');
          setLoading(false);
          return;
        }

        const pdfData = snapshot.val();
        const base64Data = pdfData.pdfData;

        if (!base64Data) {
          setError('Dados do PDF não encontrados');
          setLoading(false);
          return;
        }

        // Criar URL do PDF a partir do base64 (usando data URL para melhor compatibilidade mobile)
        const dataUrl = `data:application/pdf;base64,${base64Data}`;
        setPdfUrl(dataUrl);
        pdfUrlRef.current = dataUrl;
        setFileName(pdfData.fileName || 'documento.pdf');

        // Marcar como visualizado
        try {
          await markPDFAsViewed(pdfId);
        } catch (err) {
          console.error('Erro ao marcar PDF como visualizado:', err);
        }

        setLoading(false);
      } catch (err) {
        console.error('Erro ao carregar PDF:', err);
        setError('Erro ao carregar o PDF. Tente novamente.');
        setLoading(false);
      }
    }

    loadPDF();

    // Limpar URL ao desmontar (data URLs não precisam de revoke, mas mantemos para compatibilidade)
    return () => {
      if (pdfUrlRef.current && pdfUrlRef.current.startsWith('blob:')) {
        URL.revokeObjectURL(pdfUrlRef.current);
      }
    };
  }, [pdfId]);

  // Função para download do PDF
  const handleDownload = () => {
    if (pdfUrl) {
      const link = document.createElement('a');
      link.href = pdfUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  if (loading) {
    return (
      <div className="ns-page" style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh',
        backgroundColor: '#f8fafc'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>📄</div>
          <p style={{ fontSize: '18px', color: '#64748b' }}>Carregando PDF...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="ns-page" style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh' 
      }}>
        <div style={{ textAlign: 'center' }}>
          <h2 style={{ color: '#ef4444', marginBottom: '16px' }}>Erro</h2>
          <p style={{ marginBottom: '24px' }}>{error}</p>
          <Button onClick={() => navigate(-1)}>Voltar</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="ns-page" style={{ 
      display: 'flex', 
      flexDirection: 'column', 
      height: '100vh',
      padding: 0,
      margin: 0
    }}>
      <div style={{ 
        padding: '16px', 
        backgroundColor: '#f8fafc', 
        borderBottom: '1px solid #e2e8f0',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <div>
          <h2 style={{ margin: 0, fontSize: '18px' }}>{fileName}</h2>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <Button variant="secondary" onClick={handleDownload}>
            📥 Download
          </Button>
          <Button variant="secondary" onClick={() => navigate(-1)}>
            ← Voltar
          </Button>
        </div>
      </div>
      <div style={{ 
        flex: 1, 
        overflow: 'hidden',
        backgroundColor: '#525252',
        position: 'relative'
      }}>
        {pdfUrl && (
          <>
            {/* Tentar iframe primeiro (melhor para desktop) */}
            <iframe
              src={pdfUrl}
              style={{
                width: '100%',
                height: '100%',
                border: 'none',
                display: 'block'
              }}
              title="PDF Viewer"
              onLoad={() => {
                console.log('PDF carregado com sucesso no iframe');
              }}
              onError={() => {
                console.error('Erro ao carregar PDF no iframe, tentando embed...');
                // Se iframe falhar, tentar embed
                const embedEl = document.querySelector('embed[type="application/pdf"]') as HTMLElement;
                const iframeEl = document.querySelector('iframe[title="PDF Viewer"]') as HTMLElement;
                if (embedEl && iframeEl) {
                  iframeEl.style.display = 'none';
                  embedEl.style.display = 'block';
                }
              }}
            />
            {/* Fallback para mobile - embed direto */}
            <embed
              src={pdfUrl}
              type="application/pdf"
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                display: 'none'
              }}
            />
            {/* Fallback adicional - object tag */}
            <object
              data={pdfUrl}
              type="application/pdf"
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                display: 'none'
              }}
            >
              <p style={{ padding: '20px', color: '#fff', textAlign: 'center' }}>
                Seu navegador não suporta visualização de PDF. 
                <a 
                  href={pdfUrl} 
                  download={fileName}
                  style={{ color: '#0ea5e9', textDecoration: 'underline', marginLeft: '8px' }}
                >
                  Clique aqui para fazer o download
                </a>
              </p>
            </object>
          </>
        )}
      </div>
    </div>
  );
};

